import { useState, useEffect } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Plus, Trash2, Edit2, Search, BookOpen, Users, Eye, ExternalLink } from "lucide-react"
import { Pagination } from "@/components/pagination"
import { toast } from "sonner"

interface DictionaryEntry {
  id: string
  keyword: string
  category: string
  addedAt: string
  source: "manual" | "ai_suggestion"
  frequency: number
}

interface AccountEntry {
  id: string
  accountName: string
  accountUrl: string
  platform: string
  status: "need_to_track" | "safe" | "closed"
  addedAt: string
  lastChecked?: string
  notes?: string
}

export default function DictionaryPage() {
  // Keywords Dictionary State
  const [entries, setEntries] = useState<DictionaryEntry[]>([])
  const [searchTerm, setSearchTerm] = useState("")
  const [filterCategory, setFilterCategory] = useState<string>("all")
  const [showAddForm, setShowAddForm] = useState(false)
  const [newKeyword, setNewKeyword] = useState("")
  const [newCategory, setNewCategory] = useState("promotion")
  const [currentPage, setCurrentPage] = useState(1)

  // Account Dictionary State
  const [accounts, setAccounts] = useState<AccountEntry[]>([])
  const [accountSearchTerm, setAccountSearchTerm] = useState("")
  const [accountFilterPlatform, setAccountFilterPlatform] = useState<string>("all")
  const [accountFilterStatus, setAccountFilterStatus] = useState<string>("all")
  const [showAddAccountForm, setShowAddAccountForm] = useState(false)
  const [newAccount, setNewAccount] = useState<{
    accountName: string
    accountUrl: string
    platform: string
    status: "need_to_track" | "safe" | "closed"
    notes: string
  }>({
    accountName: "",
    accountUrl: "",
    platform: "Instagram",
    status: "need_to_track",
    notes: "",
  })
  const [accountCurrentPage, setAccountCurrentPage] = useState(1)
  const [selectedAccount, setSelectedAccount] = useState<AccountEntry | null>(null)
  const [accountDialogOpen, setAccountDialogOpen] = useState(false)

  const itemsPerPage = 10

  useEffect(() => {
    // Load keywords dictionary
    const savedEntries = localStorage.getItem("dictionary_keywords")
    if (savedEntries) {
      setEntries(JSON.parse(savedEntries))
    } else {
      const mockEntries: DictionaryEntry[] = [
        { id: "1", keyword: "bonus", category: "promotion", addedAt: "2024-01-10", source: "manual", frequency: 245 },
        { id: "2", keyword: "jackpot", category: "promotion", addedAt: "2024-01-10", source: "manual", frequency: 189 },
        { id: "3", keyword: "menang besar", category: "promotion", addedAt: "2024-01-12", source: "ai_suggestion", frequency: 156 },
        { id: "4", keyword: "deposit", category: "transaction", addedAt: "2024-01-10", source: "manual", frequency: 312 },
        { id: "5", keyword: "withdraw", category: "transaction", addedAt: "2024-01-10", source: "manual", frequency: 278 },
        { id: "6", keyword: "betting", category: "activity", addedAt: "2024-01-11", source: "manual", frequency: 201 },
        { id: "7", keyword: "odds", category: "activity", addedAt: "2024-01-11", source: "manual", frequency: 167 },
        { id: "8", keyword: "live betting", category: "activity", addedAt: "2024-01-13", source: "ai_suggestion", frequency: 98 },
        { id: "9", keyword: "free spin", category: "promotion", addedAt: "2024-01-14", source: "ai_suggestion", frequency: 134 },
        { id: "10", keyword: "cashback", category: "promotion", addedAt: "2024-01-14", source: "manual", frequency: 223 },
      ]
      setEntries(mockEntries)
      localStorage.setItem("dictionary_keywords", JSON.stringify(mockEntries))
    }

    // Load account dictionary
    const savedAccounts = localStorage.getItem("dictionary_accounts")
    if (savedAccounts) {
      setAccounts(JSON.parse(savedAccounts))
    } else {
      const mockAccounts: AccountEntry[] = [
        { id: "1", accountName: "@promo_judi_88", accountUrl: "https://instagram.com/promo_judi_88", platform: "Instagram", status: "need_to_track", addedAt: "2024-01-10" },
        { id: "2", accountName: "@judi_pro_id", accountUrl: "https://instagram.com/judi_pro_id", platform: "Instagram", status: "need_to_track", addedAt: "2024-01-11" },
        { id: "3", accountName: "Judi Online Terpercaya", accountUrl: "https://facebook.com/groups/judi-online", platform: "Facebook", status: "safe", addedAt: "2024-01-12", lastChecked: "2024-01-15" },
        { id: "4", accountName: "@betting_pro", accountUrl: "https://tiktok.com/@betting_pro", platform: "TikTok", status: "closed", addedAt: "2024-01-10", lastChecked: "2024-01-14", notes: "Account sudah ditutup oleh platform" },
        { id: "5", accountName: "@judi_online_24", accountUrl: "https://twitter.com/judi_online_24", platform: "Twitter", status: "need_to_track", addedAt: "2024-01-13" },
      ]
      setAccounts(mockAccounts)
      localStorage.setItem("dictionary_accounts", JSON.stringify(mockAccounts))
    }
  }, [])

  // Keywords Dictionary Functions
  const filteredEntries = entries.filter((entry) => {
    const matchesSearch = entry.keyword.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategory = filterCategory === "all" || entry.category === filterCategory
    return matchesSearch && matchesCategory
  })

  const totalPages = Math.ceil(filteredEntries.length / itemsPerPage)
  const paginatedEntries = filteredEntries.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  )

  const handleAddKeyword = () => {
    if (newKeyword.trim()) {
      const newEntry: DictionaryEntry = {
        id: Date.now().toString(),
        keyword: newKeyword,
        category: newCategory,
        addedAt: new Date().toISOString().split("T")[0],
        source: "manual",
        frequency: 0,
      }
      const updated = [...entries, newEntry]
      setEntries(updated)
      localStorage.setItem("dictionary_keywords", JSON.stringify(updated))
      setNewKeyword("")
      setShowAddForm(false)
      setCurrentPage(1)
      toast.success("Keyword added", { description: `"${newKeyword}" has been added to dictionary` })
    }
  }

  const handleDeleteEntry = (id: string) => {
    const updated = entries.filter((entry) => entry.id !== id)
    setEntries(updated)
    localStorage.setItem("dictionary_keywords", JSON.stringify(updated))
    setCurrentPage(1)
    toast.success("Keyword deleted")
  }

  // Account Dictionary Functions
  const filteredAccounts = accounts.filter((account) => {
    const matchesSearch = account.accountName.toLowerCase().includes(accountSearchTerm.toLowerCase()) ||
      account.accountUrl.toLowerCase().includes(accountSearchTerm.toLowerCase())
    const matchesPlatform = accountFilterPlatform === "all" || account.platform === accountFilterPlatform
    const matchesStatus = accountFilterStatus === "all" || account.status === accountFilterStatus
    return matchesSearch && matchesPlatform && matchesStatus
  })

  const accountTotalPages = Math.ceil(filteredAccounts.length / itemsPerPage)
  const paginatedAccounts = filteredAccounts.slice(
    (accountCurrentPage - 1) * itemsPerPage,
    accountCurrentPage * itemsPerPage
  )

  const handleAddAccount = () => {
    if (newAccount.accountName.trim() && newAccount.accountUrl.trim()) {
      const newEntry: AccountEntry = {
        id: Date.now().toString(),
        accountName: newAccount.accountName,
        accountUrl: newAccount.accountUrl,
        platform: newAccount.platform,
        status: newAccount.status,
        addedAt: new Date().toISOString().split("T")[0],
        notes: newAccount.notes || undefined,
      }
      const updated = [...accounts, newEntry]
      setAccounts(updated)
      localStorage.setItem("dictionary_accounts", JSON.stringify(updated))
      setNewAccount({ accountName: "", accountUrl: "", platform: "Instagram", status: "need_to_track", notes: "" })
      setShowAddAccountForm(false)
      setAccountCurrentPage(1)
      toast.success("Account added", { description: `"${newAccount.accountName}" has been added to dictionary` })
    }
  }

  const handleUpdateAccountStatus = (id: string, status: AccountEntry["status"]) => {
    const updated = accounts.map(account =>
      account.id === id
        ? { ...account, status, lastChecked: new Date().toISOString().split("T")[0] }
        : account
    )
    setAccounts(updated)
    localStorage.setItem("dictionary_accounts", JSON.stringify(updated))
    toast.success("Status updated", { description: `Account status changed to ${status.replace("_", " ")}` })
  }

  const handleDeleteAccount = (id: string) => {
    const updated = accounts.filter((account) => account.id !== id)
    setAccounts(updated)
    localStorage.setItem("dictionary_accounts", JSON.stringify(updated))
    setAccountCurrentPage(1)
    toast.success("Account deleted")
  }

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      promotion: "bg-pink-100 text-pink-800",
      transaction: "bg-blue-100 text-blue-800",
      activity: "bg-purple-100 text-purple-800",
    }
    return colors[category] || "bg-gray-100 text-gray-800"
  }

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      need_to_track: "bg-yellow-100 text-yellow-800",
      safe: "bg-green-100 text-green-800",
      closed: "bg-red-100 text-red-800",
    }
    return colors[status] || "bg-gray-100 text-gray-800"
  }

  const getPlatformColor = (platform: string) => {
    const colors: Record<string, string> = {
      Instagram: "bg-pink-100 text-pink-800",
      Facebook: "bg-blue-100 text-blue-800",
      TikTok: "bg-black text-white",
      Twitter: "bg-sky-100 text-sky-800",
      YouTube: "bg-red-100 text-red-800",
      Website: "bg-gray-100 text-gray-800",
    }
    return colors[platform] || "bg-gray-100 text-gray-800"
  }

  const categories = ["all", "promotion", "transaction", "activity"]
  const platforms = ["all", "Instagram", "Facebook", "TikTok", "Twitter", "YouTube", "Website"]
  const statuses = ["all", "need_to_track", "safe", "closed"]

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Dictionary Management</h1>
        <p className="text-muted-foreground mt-2">
          Kelola dictionary keywords dan akun untuk tracking platform
        </p>
      </div>

      <Tabs defaultValue="keywords" className="space-y-4">
        <TabsList>
          <TabsTrigger value="keywords">
            <BookOpen className="h-4 w-4 mr-2" />
            Keywords Dictionary
          </TabsTrigger>
          <TabsTrigger value="accounts">
            <Users className="h-4 w-4 mr-2" />
            Account Dictionary
          </TabsTrigger>
        </TabsList>

        {/* Keywords Dictionary Tab */}
        <TabsContent value="keywords" className="space-y-4">
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-2xl font-bold">{entries.length}</p>
                  <p className="text-sm text-muted-foreground">Total Keywords</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-2xl font-bold">{entries.filter((e) => e.source === "ai_suggestion").length}</p>
                  <p className="text-sm text-muted-foreground">AI Suggestions</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-2xl font-bold">{entries.reduce((sum, e) => sum + e.frequency, 0)}</p>
                  <p className="text-sm text-muted-foreground">Total Detections</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-2xl font-bold">{Math.max(...entries.map((e) => e.frequency), 0)}</p>
                  <p className="text-sm text-muted-foreground">Highest Frequency</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Add New Keyword */}
          <Card>
            <CardContent className="pt-6">
              {!showAddForm ? (
                <Button onClick={() => setShowAddForm(true)} className="w-full bg-primary hover:bg-primary/90">
                  <Plus className="h-4 w-4 mr-2" />
                  Add New Keyword
                </Button>
              ) : (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="keyword">Keyword</Label>
                    <Input
                      id="keyword"
                      type="text"
                      value={newKeyword}
                      onChange={(e) => setNewKeyword(e.target.value)}
                      placeholder="Enter keyword..."
                    />
                  </div>
                  <div>
                    <Label htmlFor="category">Category</Label>
                    <select
                      id="category"
                      value={newCategory}
                      onChange={(e) => setNewCategory(e.target.value)}
                      className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                    >
                      <option value="promotion">Promotion</option>
                      <option value="transaction">Transaction</option>
                      <option value="activity">Activity</option>
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleAddKeyword} className="flex-1 bg-secondary hover:bg-secondary/90">
                      Add
                    </Button>
                    <Button onClick={() => setShowAddForm(false)} variant="outline" className="flex-1">
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Search and Filter */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Search keywords..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value)
                      setCurrentPage(1)
                    }}
                    className="pl-10"
                  />
                </div>
                <select
                  value={filterCategory}
                  onChange={(e) => {
                    setFilterCategory(e.target.value)
                    setCurrentPage(1)
                  }}
                  className="px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                >
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat.charAt(0).toUpperCase() + cat.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
            </CardContent>
          </Card>

          {/* Dictionary Entries */}
          <div className="space-y-3">
            {paginatedEntries.map((entry) => (
              <Card key={entry.id} className="hover:shadow-md transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <p className="text-lg font-semibold text-foreground">{entry.keyword}</p>
                        <Badge className={getCategoryColor(entry.category)}>{entry.category}</Badge>
                        <Badge variant={entry.source === "ai_suggestion" ? "secondary" : "outline"}>
                          {entry.source === "ai_suggestion" ? "AI Suggestion" : "Manual"}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span>Added: {entry.addedAt}</span>
                        <span>Detections: {entry.frequency}</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-destructive border-destructive/20 hover:bg-destructive/10"
                        onClick={() => handleDeleteEntry(entry.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredEntries.length === 0 && (
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-muted-foreground">No keywords found</p>
              </CardContent>
            </Card>
          )}

          {filteredEntries.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          )}
        </TabsContent>

        {/* Account Dictionary Tab */}
        <TabsContent value="accounts" className="space-y-4">
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-2xl font-bold">{accounts.length}</p>
                  <p className="text-sm text-muted-foreground">Total Accounts</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-2xl font-bold">{accounts.filter((a) => a.status === "need_to_track").length}</p>
                  <p className="text-sm text-muted-foreground">Need to Track</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-2xl font-bold">{accounts.filter((a) => a.status === "safe").length}</p>
                  <p className="text-sm text-muted-foreground">Safe</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-2xl font-bold">{accounts.filter((a) => a.status === "closed").length}</p>
                  <p className="text-sm text-muted-foreground">Closed</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Add New Account */}
          <Card>
            <CardContent className="pt-6">
              {!showAddAccountForm ? (
                <Button onClick={() => setShowAddAccountForm(true)} className="w-full bg-primary hover:bg-primary/90">
                  <Plus className="h-4 w-4 mr-2" />
                  Add New Account
                </Button>
              ) : (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="account-name">Account Name</Label>
                    <Input
                      id="account-name"
                      type="text"
                      value={newAccount.accountName}
                      onChange={(e) => setNewAccount({ ...newAccount, accountName: e.target.value })}
                      placeholder="e.g., @username or Account Name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="account-url">Account URL</Label>
                    <Input
                      id="account-url"
                      type="url"
                      value={newAccount.accountUrl}
                      onChange={(e) => setNewAccount({ ...newAccount, accountUrl: e.target.value })}
                      placeholder="https://..."
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="platform">Platform</Label>
                      <select
                        id="platform"
                        value={newAccount.platform}
                        onChange={(e) => setNewAccount({ ...newAccount, platform: e.target.value })}
                        className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                      >
                        <option value="Instagram">Instagram</option>
                        <option value="Facebook">Facebook</option>
                        <option value="TikTok">TikTok</option>
                        <option value="Twitter">Twitter</option>
                        <option value="YouTube">YouTube</option>
                        <option value="Website">Website</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="status">Status</Label>
                      <select
                        id="status"
                        value={newAccount.status}
                        onChange={(e) => {
                          const value = e.target.value as "need_to_track" | "safe" | "closed"
                          setNewAccount(prev => ({ ...prev, status: value }))
                        }}
                        className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                      >
                        <option value="need_to_track">Need to Track</option>
                        <option value="safe">Safe</option>
                        <option value="closed">Closed</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="notes">Notes (Optional)</Label>
                    <Input
                      id="notes"
                      type="text"
                      value={newAccount.notes}
                      onChange={(e) => setNewAccount({ ...newAccount, notes: e.target.value })}
                      placeholder="Additional notes..."
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleAddAccount} className="flex-1 bg-secondary hover:bg-secondary/90">
                      Add
                    </Button>
                    <Button onClick={() => setShowAddAccountForm(false)} variant="outline" className="flex-1">
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Search and Filter */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Search account name or URL..."
                    value={accountSearchTerm}
                    onChange={(e) => {
                      setAccountSearchTerm(e.target.value)
                      setAccountCurrentPage(1)
                    }}
                    className="pl-10"
                  />
                </div>
                <select
                  value={accountFilterPlatform}
                  onChange={(e) => {
                    setAccountFilterPlatform(e.target.value)
                    setAccountCurrentPage(1)
                  }}
                  className="px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                >
                  {platforms.map((platform) => (
                    <option key={platform} value={platform}>
                      {platform === "all" ? "All Platforms" : platform}
                    </option>
                  ))}
                </select>
                <select
                  value={accountFilterStatus}
                  onChange={(e) => {
                    setAccountFilterStatus(e.target.value)
                    setAccountCurrentPage(1)
                  }}
                  className="px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                >
                  {statuses.map((status) => (
                    <option key={status} value={status}>
                      {status === "all" ? "All Status" : status.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase())}
                    </option>
                  ))}
                </select>
              </div>
            </CardContent>
          </Card>

          {/* Account Entries */}
          <div className="space-y-3">
            {paginatedAccounts.map((account) => (
              <Card key={account.id} className="hover:shadow-md transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <p className="text-lg font-semibold text-foreground">{account.accountName}</p>
                        <Badge className={getPlatformColor(account.platform)}>{account.platform}</Badge>
                        <Badge className={getStatusColor(account.status)}>
                          {account.status.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase())}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2">
                        <span className="truncate max-w-md">{account.accountUrl}</span>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span>Added: {account.addedAt}</span>
                        {account.lastChecked && <span>Last Checked: {account.lastChecked}</span>}
                      </div>
                      {account.notes && (
                        <p className="text-sm text-muted-foreground mt-2 italic">{account.notes}</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedAccount(account)
                          setAccountDialogOpen(true)
                        }}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-destructive border-destructive/20 hover:bg-destructive/10"
                        onClick={() => handleDeleteAccount(account.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredAccounts.length === 0 && (
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-muted-foreground">No accounts found</p>
              </CardContent>
            </Card>
          )}

          {filteredAccounts.length > 0 && (
            <Pagination
              currentPage={accountCurrentPage}
              totalPages={accountTotalPages}
              onPageChange={setAccountCurrentPage}
            />
          )}
        </TabsContent>
      </Tabs>

      {/* Account Detail Dialog */}
      <Dialog open={accountDialogOpen} onOpenChange={setAccountDialogOpen}>
        <DialogContent className="max-w-2xl">
          {selectedAccount && (
            <>
              <DialogHeader>
                <DialogTitle>{selectedAccount.accountName}</DialogTitle>
                <DialogDescription>{selectedAccount.accountUrl}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Badge className={getPlatformColor(selectedAccount.platform)}>{selectedAccount.platform}</Badge>
                  <Badge className={getStatusColor(selectedAccount.status)}>
                    {selectedAccount.status.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase())}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Added At</Label>
                    <p className="text-sm text-foreground">{selectedAccount.addedAt}</p>
                  </div>
                  {selectedAccount.lastChecked && (
                    <div>
                      <Label>Last Checked</Label>
                      <p className="text-sm text-foreground">{selectedAccount.lastChecked}</p>
                    </div>
                  )}
                </div>
                {selectedAccount.notes && (
                  <div>
                    <Label>Notes</Label>
                    <p className="text-sm text-foreground">{selectedAccount.notes}</p>
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Update Status</Label>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant={selectedAccount.status === "need_to_track" ? "default" : "outline"}
                      onClick={() => handleUpdateAccountStatus(selectedAccount.id, "need_to_track")}
                    >
                      Need to Track
                    </Button>
                    <Button
                      size="sm"
                      variant={selectedAccount.status === "safe" ? "default" : "outline"}
                      onClick={() => handleUpdateAccountStatus(selectedAccount.id, "safe")}
                    >
                      Safe
                    </Button>
                    <Button
                      size="sm"
                      variant={selectedAccount.status === "closed" ? "default" : "outline"}
                      onClick={() => handleUpdateAccountStatus(selectedAccount.id, "closed")}
                    >
                      Closed
                    </Button>
                  </div>
                </div>
                <div className="pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => window.open(selectedAccount.accountUrl, '_blank')}
                    className="w-full"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open URL
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
